import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { DateTime } from 'luxon'
import { HelpCircle, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Heading, Text } from '@/components/ui/typography'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { RotationShiftBoundary, Staff } from '@/lib/api/directory.client'
import RotationGrid from './RotationGrid'
import RotationCsvDialog from './RotationCsvDialog'
import { memberColor } from './member-color'

const SHIFT_OPTIONS: Record<
  RotationShiftBoundary,
  { label: string; help: string }
> = {
  CALENDAR_DAY: {
    label: 'Calendar day',
    help: 'Each person is on call from 12:00am to 11:59pm.',
  },
  BUSINESS_HOURS: {
    label: 'Business hours',
    help: "Each person is on call from the day's start time until the next business day's start time. On closed days, shifts default to 24 hours. Set the time it starts and ends.",
  },
}
const SHIFT_BOUNDARIES = Object.keys(SHIFT_OPTIONS) as RotationShiftBoundary[]

export function RotationSection(props: {
  members: Staff[]
  baseAssignments: Record<string, string | null>
  edits: Record<string, string | null>
  unknownByDate: Record<string, string>
  fallbackStaffId: string | null
  shiftBoundary: RotationShiftBoundary
  closedDayStart: string | null
  hasOfficeHours: boolean
  todayIso: string
  minEditableDate: string
  onAssign: (date: string, staffId: string | null) => void
  onFallbackChange: (staffId: string | null) => void
  onShiftBoundaryChange: (shiftBoundary: RotationShiftBoundary) => void
  onClosedDayStartChange: (closedDayStart: string | null) => void
  onCsvApply: (result: {
    edits: Record<string, string | null>
    unknownByDate: Record<string, string>
  }) => void
}) {
  const {
    members,
    baseAssignments,
    edits,
    unknownByDate,
    fallbackStaffId,
    shiftBoundary,
    closedDayStart,
    hasOfficeHours,
    todayIso,
    minEditableDate,
    onAssign,
    onFallbackChange,
    onShiftBoundaryChange,
    onClosedDayStartChange,
    onCsvApply,
  } = props
  const [csvOpen, setCsvOpen] = useState(false)
  // closedDayStart null = midnight; else a custom HH:MM. Offer every half
  // hour, plus a saved value that falls between them.
  const closedDayStartTimes = Array.from({ length: 48 }, (_, slot) =>
    DateTime.fromObject({
      hour: Math.floor(slot / 2),
      minute: (slot % 2) * 30,
    }).toFormat('HH:mm'),
  )
  if (closedDayStart && !closedDayStartTimes.includes(closedDayStart)) {
    closedDayStartTimes.push(closedDayStart)
    closedDayStartTimes.sort()
  }
  const timeLabel = (hhmm: string) =>
    DateTime.fromFormat(hhmm, 'HH:mm').toFormat('h:mma').toLowerCase()

  const assignments = useMemo(
    () => ({ ...baseAssignments, ...edits }),
    [baseAssignments, edits],
  )

  // Lazy-loaded scroll: render a loaded band of weeks (a few before today, more
  // after) inside a ~4-week-tall scroll container; scrolling near either edge
  // grows the band, up to a bounded window (1 month back, 12 months forward) so
  // the calendar doesn't scroll endlessly into the empty past/future. Past days
  // render grayed + read-only via minEditableDate.
  const thisMonday = DateTime.fromISO(todayIso).startOf('week')
  const maxWeeksBefore = Math.max(
    0,
    Math.ceil(thisMonday.diff(thisMonday.minus({ months: 1 }), 'weeks').weeks),
  )
  const maxWeeksAfter = Math.max(
    0,
    Math.ceil(thisMonday.plus({ months: 12 }).diff(thisMonday, 'weeks').weeks),
  )
  const CHUNK = 4
  const [weeksBefore, setWeeksBefore] = useState(2)
  const [weeksAfter, setWeeksAfter] = useState(8)
  const scrollRef = useRef<HTMLDivElement>(null)
  const todayRowRef = useRef<HTMLTableRowElement>(null)
  // Preserve scroll position when we prepend older weeks (which shifts content).
  const pendingPrepend = useRef<{ prevHeight: number; prevTop: number } | null>(
    null,
  )

  const gridMembers = members.map(member => ({
    id: member.id,
    name: member.name,
  }))
  const weeks = useMemo(() => {
    const first = thisMonday.minus({ weeks: weeksBefore })
    const total = weeksBefore + 1 + weeksAfter
    return Array.from(
      { length: total },
      (_, i) => first.plus({ weeks: i }).toISODate() ?? '',
    )
    // thisMonday derives from todayIso (stable per render)
  }, [weeksBefore, weeksAfter, todayIso])

  const thisMondayIso = thisMonday.toISODate() ?? ''

  // Month/year "in focus" — the month of the topmost visible week, shown next
  // to the Today button and kept in sync while scrolling.
  const [focusMonday, setFocusMonday] = useState(thisMondayIso)
  const updateFocusMonth = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const headerHeight =
      el.querySelector('thead')?.getBoundingClientRect().height ?? 0
    const rows = el.querySelectorAll<HTMLTableRowElement>('tr[data-week]')
    for (const row of rows) {
      if (row.offsetTop + row.offsetHeight > el.scrollTop + headerHeight) {
        if (row.dataset.week) setFocusMonday(row.dataset.week)
        return
      }
    }
  }, [])
  useEffect(() => {
    updateFocusMonth()
  }, [updateFocusMonth])

  const onScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    updateFocusMonth()
    if (el.scrollTop < 60 && weeksBefore < maxWeeksBefore) {
      pendingPrepend.current = {
        prevHeight: el.scrollHeight,
        prevTop: el.scrollTop,
      }
      setWeeksBefore(n => Math.min(maxWeeksBefore, n + CHUNK))
    } else if (
      el.scrollTop + el.clientHeight > el.scrollHeight - 60 &&
      weeksAfter < maxWeeksAfter
    ) {
      setWeeksAfter(n => Math.min(maxWeeksAfter, n + CHUNK))
    }
  }, [updateFocusMonth, weeksBefore, weeksAfter, maxWeeksBefore, maxWeeksAfter])

  // After prepending older weeks, keep the viewport anchored on the same row.
  useLayoutEffect(() => {
    const el = scrollRef.current
    const pending = pendingPrepend.current
    if (el && pending) {
      el.scrollTop = pending.prevTop + (el.scrollHeight - pending.prevHeight)
      pendingPrepend.current = null
      updateFocusMonth()
    }
  }, [weeksBefore, updateFocusMonth])

  const scrollToToday = () => {
    todayRowRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }

  // Same look as the day-cell picker: color dot + plain-weight name. Member
  // index drives the dot color, matching the grid and the Members list.
  const memberDot = (index: number) => (
    <span
      className={cn(
        'h-2.5 w-2.5 shrink-0 rounded-full',
        memberColor(index).swatch,
      )}
    />
  )
  const fallbackItems: SearchDropdownItem<Staff>[] = members.map(
    (member, index) => ({
      id: member.id,
      label: member.name,
      icon: memberDot(index),
      data: member,
    }),
  )
  // The dropdown can't clear a pick, so "No fallback" is an item of its own.
  const NO_FALLBACK = '__none__'
  const fallbackOptions: SearchDropdownItem<Staff>[] = [
    { id: NO_FALLBACK, label: 'No fallback' },
    ...fallbackItems,
  ]
  const fallbackSelected =
    fallbackItems.find(item => item.id === fallbackStaffId) ?? null
  const fallbackIndex = members.findIndex(
    member => member.id === fallbackStaffId,
  )
  const fallbackName =
    fallbackIndex >= 0 ? members[fallbackIndex].name : undefined

  return (
    <section className="space-y-4">
      <Heading variant="subsection-title">On-call calendar</Heading>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <Text variant="body-sm" className="font-medium">
            Shift based on
          </Text>
          <Select
            value={shiftBoundary}
            onValueChange={value =>
              onShiftBoundaryChange(value as RotationShiftBoundary)
            }
          >
            <SelectTrigger className="w-40" aria-label="Shift based on">
              {/* Items carry help text; show only the name when closed. */}
              <SelectValue>{SHIFT_OPTIONS[shiftBoundary].label}</SelectValue>
            </SelectTrigger>
            <SelectContent className="max-w-sm">
              {SHIFT_BOUNDARIES.map(value => (
                <SelectItem
                  key={value}
                  value={value}
                  disabled={value === 'BUSINESS_HOURS' && !hasOfficeHours}
                >
                  <div className="flex flex-col gap-0.5">
                    <span>{SHIFT_OPTIONS[value].label}</span>
                    <span className="text-muted-foreground text-xs whitespace-normal">
                      {SHIFT_OPTIONS[value].help}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {shiftBoundary === 'BUSINESS_HOURS' && hasOfficeHours && (
            <>
              <Text variant="body-sm" className="font-medium">
                Closed days start at
              </Text>
              <Select
                value={closedDayStart === null ? 'MIDNIGHT' : 'CUSTOM'}
                onValueChange={value =>
                  // Seed a custom time; the admin changes it in the next dropdown.
                  onClosedDayStartChange(value === 'CUSTOM' ? '08:00' : null)
                }
              >
                <SelectTrigger
                  className="w-32"
                  aria-label="Closed days start at"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MIDNIGHT">Midnight</SelectItem>
                  <SelectItem value="CUSTOM">Custom</SelectItem>
                </SelectContent>
              </Select>
              {closedDayStart !== null && (
                <Select
                  value={closedDayStart}
                  onValueChange={onClosedDayStartChange}
                >
                  <SelectTrigger
                    className="w-32"
                    aria-label="Closed day start time"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {closedDayStartTimes.map(hhmm => (
                      <SelectItem key={hhmm} value={hhmm}>
                        {timeLabel(hhmm)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </>
          )}
        </div>
        {!hasOfficeHours && (
          <Text variant="body-sm" className="text-muted-foreground">
            {shiftBoundary === 'BUSINESS_HOURS'
              ? 'This company has no office hours, so shifts follow the calendar day until office hours are set.'
              : 'Business hours needs office hours. Set them in company settings to use it.'}
          </Text>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-1.5">
            <Text variant="body-sm" className="font-medium">
              Fallback
            </Text>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="What is the fallback person?"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <HelpCircle className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  Covers any day with no one assigned.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
          <SearchDropdown<Staff>
            items={fallbackOptions}
            selectedItems={fallbackSelected}
            onSelect={item => {
              const picked = Array.isArray(item) ? item[0] : item
              onFallbackChange(
                picked && picked.id !== NO_FALLBACK ? picked.id : null,
              )
            }}
            placeholder="Pick a fallback person"
            emptyMessage="No members yet — add people in Members above."
            buttonClassName="w-64"
            icon={fallbackIndex >= 0 ? memberDot(fallbackIndex) : undefined}
            renderItem={item => (
              <>
                {item.icon}
                <span
                  className={cn(
                    'truncate',
                    item.id === NO_FALLBACK && 'text-muted-foreground',
                  )}
                >
                  {item.label}
                </span>
              </>
            )}
          />
        </div>
        <div className="flex items-center gap-3">
          <Text variant="body-sm" className="font-medium">
            {DateTime.fromISO(focusMonday).toFormat('LLLL yyyy')}
          </Text>
          <Button variant="outline" size="sm" onClick={scrollToToday}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCsvOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Upload CSV
          </Button>
        </div>
      </div>

      {/* Lazy-loaded scroll: ~4 weeks visible; scroll to load older/newer.
          The grid is always hand-editable; past days are grayed + locked. */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="max-h-[26rem] overflow-auto rounded-lg border"
      >
        <RotationGrid
          weeks={weeks}
          assignmentsByDate={assignments}
          members={gridMembers}
          todayIso={todayIso}
          minEditableDate={minEditableDate}
          fallbackName={fallbackName}
          todayWeekRef={todayRowRef}
          todayWeekMonday={thisMondayIso}
          unknownByDate={unknownByDate}
          onAssign={onAssign}
        />
      </div>

      {csvOpen && (
        <RotationCsvDialog
          members={gridMembers}
          todayIso={todayIso}
          onClose={() => setCsvOpen(false)}
          onApply={result => {
            onCsvApply(result)
            setCsvOpen(false)
          }}
        />
      )}
    </section>
  )
}
